import React, { useReducer, useEffect } from "react";
import { LatLng, Map } from "leaflet";

import { freeze, unFreeze } from "../utilities/bodyFreeze";

/**
 * Reducer
 */

enum UiActionTypes {
  OPEN_DRAWER = "OPEN_DRAWER",
  CLOSE_DRAWER = "CLOSE_DRAWER",
  OPEN_MODAL = "OPEN_MODAL",
  CLOSE_MODAL = "CLOSE_MODAL",
  OPEN_DIALOG = "OPEN_DIALOG",
  CLOSE_DIALOG = "CLOSE_DIALOG",
  SELECT_IMAGES = "SELECT_IMAGES",
  SHOW_ONBOARDING = "SHOW_ONBOARDING",
  ADD_NOTIFICATION = "ADD_NOTIFICATION",
  REMOVE_NOTIFICATION = "REMOVE_NOTIFICATION",
  OPEN_PICKER = "OPEN_PICKER",
  CLOSE_PICKER = "CLOSE_PICKER",
  SET_LOCATION = "SET_LOCATION",
  SET_MAP_ELEMENT = "SET_MAP_ELEMENT",
}

export type UiState = {
  touched: boolean;
  mapElement?: Map;
  picker: {
    isOpen?: boolean;
    selectedLocation?: LatLng;
  };
  modal: {
    isOpen?: boolean;
    isFullPage?: boolean;
    showClose?: boolean;
    name?: string;
  };
  dialog: {
    isOpen?: boolean;
    name?: string;
    entryId?: string;
  };
};

type UiActionPayload = {
  id?: string;
  name?: string;
  location?: LatLng;
  type?: "success" | "error" | "info";
  duration?: number;
  isFullPage?: boolean;
  mapElement?: Map;
};

type UiAction = {
  type: UiActionTypes;
  payload?: UiActionPayload;
};

/**
 * Reducer
 */

export const uiReducer = (state: UiState, action: UiAction): UiState => {
  switch (action.type) {
    case UiActionTypes.OPEN_MODAL:
      return {
        ...state,
        touched: true,
        modal: {
          name: action.payload?.name,
          isOpen: true,
          isFullPage: action.payload?.isFullPage,
        },
      };
    case UiActionTypes.CLOSE_MODAL:
      return {
        ...state,
        touched: true,
        modal: {
          ...state.modal,
          isOpen: false,
          isFullPage: false,
          showClose: false,
        },
      };
    case UiActionTypes.OPEN_DIALOG:
      return {
        ...state,
        touched: true,
        dialog: {
          name: action.payload?.name,
          entryId: action.payload?.id,
          isOpen: true,
        },
      };
    case UiActionTypes.CLOSE_DIALOG:
      return {
        ...state,
        touched: true,
        dialog: { ...state.dialog, entryId: undefined, isOpen: false },
      };
    case UiActionTypes.OPEN_PICKER:
      return {
        ...state,
        touched: true,
        picker: {
          ...state.picker,
          isOpen: true,
        },
      };
    case UiActionTypes.CLOSE_PICKER:
      return {
        ...state,
        touched: true,
        picker: {
          ...state.picker,
          isOpen: false,
        },
      };
    case UiActionTypes.SET_LOCATION:
      return {
        ...state,
        touched: true,
        picker: {
          ...state.picker,
          selectedLocation: action.payload?.location,
        },
      };
    case UiActionTypes.SET_MAP_ELEMENT:
      return {
        ...state,
        touched: true,
        mapElement: action.payload?.mapElement,
      };
    default:
      return state;
  }
};

/**
 * Create context
 */

type ContextProps = {
  state: UiState;
  openModal: (props: UiActionPayload) => void;
  closeModal: () => void;
  openDialog: (props: UiActionPayload) => void;
  closeDialog: () => void;
  openPicker: () => void;
  closePicker: () => void;
  setLocation: (location: LatLng) => void;
  setMapElement: (mapElement: Map) => void;
};

const initialState: ContextProps = {
  state: {
    touched: false,
    mapElement: undefined,
    picker: { isOpen: false },
    dialog: { isOpen: false },
    modal: { isOpen: false, isFullPage: false },
  },
  openModal: () => null,
  closeModal: () => null,
  openDialog: () => null,
  closeDialog: () => null,
  openPicker: () => null,
  closePicker: () => null,
  setLocation: () => null,
  setMapElement: () => null,
};

const UiContext = React.createContext<ContextProps>(initialState);

/**
 * Provider
 */

type Props = {
  children: JSX.Element[] | JSX.Element | string;
};

export const UiProvider: React.FC<Props> = ({ children }) => {
  const [state, dispatch] = useReducer(uiReducer, initialState.state);

  // Listen for changes and apply bodyfreeze if needed
  useEffect(() => {
    if (!state.touched) {
      return;
    }
    if (state?.modal?.isOpen || state?.dialog?.isOpen) {
      return freeze();
    }
    if (!state?.modal?.isOpen && !state?.dialog?.isOpen) {
      return unFreeze();
    }
  }, [state]);

  const openModal = (props: UiActionPayload) => {
    // If modal is already open, allow it to close first
    if (state.modal?.isOpen) {
      dispatch({
        type: UiActionTypes.CLOSE_MODAL,
      });
      setTimeout(() => {
        dispatch({
          type: UiActionTypes.OPEN_MODAL,
          payload: props,
        });
      }, 0);
    } else {
      dispatch({
        type: UiActionTypes.OPEN_MODAL,
        payload: props,
      });
    }
  };

  const closeModal = () =>
    dispatch({
      type: UiActionTypes.CLOSE_MODAL,
    });

  const openDialog = (props: UiActionPayload) =>
    dispatch({
      type: UiActionTypes.OPEN_DIALOG,
      payload: props,
    });

  const closeDialog = () =>
    dispatch({
      type: UiActionTypes.CLOSE_DIALOG,
    });

  const openPicker = () =>
    dispatch({
      type: UiActionTypes.OPEN_PICKER,
    });

  const closePicker = () =>
    dispatch({
      type: UiActionTypes.CLOSE_PICKER,
    });

  const setLocation = (location: LatLng) =>
    dispatch({
      type: UiActionTypes.SET_LOCATION,
      payload: { location },
    });

  const setMapElement = (mapElement: Map) =>
    dispatch({
      type: UiActionTypes.SET_MAP_ELEMENT,
      payload: { mapElement },
    });

  return (
    <UiContext.Provider
      value={{
        state,
        openModal,
        closeModal,
        openDialog,
        closeDialog,
        openPicker,
        closePicker,
        setLocation,
        setMapElement,
      }}
    >
      {children}
    </UiContext.Provider>
  );
};

export default UiContext;
